import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface TrialStatus {
  isTrialExpired: boolean;
  hasActiveSubscription: boolean;
  daysRemaining: number | null;
  isLoading: boolean;
  plan: string | null;
  sessionInvalid: boolean;
}

export const useTrialStatus = (): TrialStatus => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["trial-status", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return { isTrialExpired: false, hasActiveSubscription: false, daysRemaining: null, plan: null, sessionInvalid: false };
      }

      // Check Stripe subscription status FIRST (most reliable source of truth)
      try {
        const { data: subData, error: subError } = await supabase.functions.invoke("check-subscription");
        
        // Handle 401 response (user not found / session expired)
        if (subData?.code === "USER_NOT_FOUND" || subData?.code === "NO_AUTH") {
          await supabase.auth.signOut();
          return { isTrialExpired: false, hasActiveSubscription: false, daysRemaining: null, plan: null, sessionInvalid: true };
        }
        
        // If user has an active Stripe subscription, they have access - period
        if (subData?.subscribed) {
          return { 
            isTrialExpired: false, 
            hasActiveSubscription: true, 
            daysRemaining: null, 
            plan: subData?.plan || "starter",
            sessionInvalid: false
          };
        }
        
        // No Stripe subscription - check if edge function returned trial data
        if (subData?.is_in_trial !== undefined) {
          return {
            isTrialExpired: !subData.is_in_trial,
            hasActiveSubscription: false,
            daysRemaining: subData.days_remaining ?? 0,
            plan: subData?.plan || "starter",
            sessionInvalid: false
          };
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error checking subscription:", error);
        }
      }

      // No active Stripe subscription - check profile for trial status
      const { data: profile } = await supabase
        .from("profiles")
        .select("plan, trial_ends_at")
        .eq("id", user.id)
        .single();

      const plan = profile?.plan || "starter";
      const trialEndsAt = profile?.trial_ends_at;

      // Calculate trial status
      if (!trialEndsAt) {
        // No trial_ends_at set - treat as expired (no free tier)
        return { isTrialExpired: true, hasActiveSubscription: false, daysRemaining: 0, plan, sessionInvalid: false };
      }

      const now = new Date();
      const trialEnd = new Date(trialEndsAt);
      const isExpired = now > trialEnd;
      const daysRemaining = isExpired ? 0 : Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      return {
        isTrialExpired: isExpired,
        hasActiveSubscription: false,
        daysRemaining,
        plan,
        sessionInvalid: false,
      };
    },
    enabled: !!user?.id,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 60000, // Refetch every minute
  });

  return {
    isTrialExpired: data?.isTrialExpired ?? false,
    hasActiveSubscription: data?.hasActiveSubscription ?? false,
    daysRemaining: data?.daysRemaining ?? null,
    isLoading,
    plan: data?.plan ?? null,
    sessionInvalid: data?.sessionInvalid ?? false,
  };
};
